import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Button,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Reusable confirmation dialog.
 *
 * Usage:
 *   const confirmRef = useRef();
 *
 *   // Simple confirm
 *   confirmRef.current.open({
 *     title: 'Delete Test',
 *     message: 'Are you sure?',
 *     onConfirm: () => handleDelete(),
 *   });
 *
 *   // With custom button labels and colors
 *   confirmRef.current.open({
 *     title: 'Warning',
 *     message: '3 tests will lose their subject.',
 *     confirmLabel: 'Remove Anyway',
 *     confirmColor: 'error',
 *     onConfirm: () => handleRemove(),
 *   });
 *
 *   <ConfirmDialog ref={confirmRef} />
 */
const ConfirmDialog = forwardRef((_, ref) => {
    const [open, setOpen] = useState(false);
    const [config, setConfig] = useState({});

    useImperativeHandle(ref, () => ({
        open: (cfg) => {
            setConfig(cfg);
            setOpen(true);
        },
    }), []);

    const handleClose = () => {
        setOpen(false);
        config.onCancel?.();
    };

    const handleConfirm = () => {
        setOpen(false);
        config.onConfirm?.();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {config.icon || <WarningAmberIcon color="warning" />}
                {config.title || 'Confirm'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>{config.message}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {config.cancelLabel || 'Cancel'}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color={config.confirmColor || 'error'}
                >
                    {config.confirmLabel || 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default ConfirmDialog;
